import React, {useState} from 'react';
import {Box, Button, IconButton, Typography} from '@mui/material';
import {Formik} from 'formik';
import DeleteIcon from '@mui/icons-material/Delete';
import {useSnackbar} from 'notistack';
import ActionModal from '../../components/ContactActions/ActionModal';
import {handleFileEvent, onDeleteUploadFileFactory, toBase64} from './utilsV1';
import {useClientFilesV1} from './useClientFilesV1';
import {useAccountContext} from '../../contexts/AccountContext';
import {formatBytes} from '../../utils/formatBytes';
import AutoField, {ActionType, SelectField} from '../../components/AutoForm/AutoFieldNew';
import {SaltusAny} from '../../utils/SaltusAny';

const categoriesField: SelectField = {
  id: 'category',
  label: 'Document category',
  type: ActionType.select,
  options: [
    {label: 'Application forms', value: 'Application forms'},
    {label: 'Transfers forms', value: 'Transfers forms'},
    {label: 'Direct Debit Mandate', value: 'Direct Debit Mandate'},
    {label: 'SIPP Contribution forms', value: 'SIPP Contribution forms'},
    {label: 'APS forms', value: 'APS forms'},
    {label: 'Expression of wishes', value: 'Expression of wishes'},
    {label: 'SIPP Income forms', value: 'SIPP Income forms'},
    {label: 'SIPP Illustrations', value: 'SIPP Illustrations'},
    {label: 'LTA declaration', value: 'LTA declaration'},
  ],
};

const getClientFilename = (
  el: File,
  onDelete: (filename: string) => void,
  index: number
) => {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" key={el.name}>
      <Box flex={2}>
        <Typography data-testid="client-filename">{el.name}</Typography>
      </Box>
      <Box flex={0.5}>
        <Typography>{formatBytes(el.size)}</Typography>
      </Box>
      <Box flex={1}>
        <AutoField
          field={{
            ...categoriesField,
            id: `category[${index}]`,
          }}
        />
      </Box>
      <Box flex={0.5} display="flex" justifyContent="end">
        <IconButton
          onClick={() => {
            onDelete(el.name);
          }}
          size="large"
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export const FileUploadModalV1 = ({
  isOpen,
  close,
}: {
  isOpen: boolean;
  close: (event: SaltusAny, reason: 'backdropClick' | 'escapeKeyDown') => void;
}) => {
  const {account} = useAccountContext();
  const {uploadClientFile} = useClientFilesV1();
  const {enqueueSnackbar} = useSnackbar();
  const [canSendFiles, setCanSendFiles] = useState(true);
  return (
    <Formik
      initialValues={{files: [], category: []}}
      onSubmit={(theseValues) => {
        setCanSendFiles(false);
        enqueueSnackbar({
          message: 'Sending file(s)...',
          variant: 'info',
          autoHideDuration: 2000,
        });
        const uploadsDone: boolean[] = [];
        theseValues.files.map(async (x: File, i: number) => {
          const fileData = await toBase64(x);
          uploadClientFile({
            variables: {
              accountId: account?.curoId, // This does not consume oneview id, but curo id
              fileData,
              fileName: x.name,
              documentType: theseValues.category[i],
            },
            onCompleted(data, clientOptions) {
              uploadsDone.push(true);
              if (uploadsDone.length === theseValues.files.length) {
                setCanSendFiles(true);
              }
              if (data.clientDocumentUpload?.ok) {
                enqueueSnackbar({
                  message: 'File uploaded.',
                  variant: 'success',
                });
                close(null, 'escapeKeyDown');
                return;
              }
              enqueueSnackbar({
                message: `File upload failed: ${data.clientDocumentUpload?.errors?.join(
                  ', '
                )}`,
                variant: 'error',
                autoHideDuration: null,
              });
            },
            onError(error, clientOptions) {
              uploadsDone.push(false);
              if (uploadsDone.length === theseValues.files.length) {
                setCanSendFiles(true);
              }
              enqueueSnackbar({
                message: 'File upload failed.',
                variant: 'error',
                autoHideDuration: null,
              });
            },
          });
        });
      }}
    >
      {({setFieldValue, handleSubmit, values}) => {
        const onDeletUploadFile = onDeleteUploadFileFactory({
          values,
          setFieldValue,
        });
        return (
          <ActionModal
            title="Upload Client files (PDF only, up to 30MB)"
            isOpen={isOpen}
            close={close}
            wide
          >
            <form onSubmit={handleSubmit} key={JSON.stringify(values)}>
              <Box>
                {values.files.length > 0 ? (
                  values.files.map((e: File, i) =>
                    getClientFilename(e, onDeletUploadFile, i)
                  )
                ) : (
                  <Typography variant="caption">No files selected</Typography>
                )}
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Button component="label" variant="contained">
                  Select files
                  <input
                    hidden
                    type="file"
                    multiple
                    accept="application/pdf"
                    onChange={(event) => {
                      handleFileEvent({
                        event,
                        setFieldValue,
                      });
                    }}
                    data-testid="file-input"
                  />
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  disabled={!canSendFiles}
                  data-testid="file-upload"
                >
                  Upload
                </Button>
              </Box>
            </form>
          </ActionModal>
        );
      }}
    </Formik>
  );
};
